import React from 'react';
import PropTypes from 'prop-types';

// Config
import { uiConfig } from '../../../../conf/resourceParameters';
import { translateUIConfigForAddEdit } from '../../../../../common/utility';

// Components
import FeedbackCard from '../../../../../components/FeedbackCard';
import Form from '../../../../../components/Form';
import { Dialog } from '../../../../../components/Dialog';
import { DialogHeader } from '../../../../../components/DialogHeader';
import { DialogBody } from '../../../../../components/DialogBody';
import { DialogFooter } from '../../../../../components/DialogFooter';
import { Button } from '../../../../../components/Button';
import { ConfirmationModal } from '../../../../../components/ConfirmationModal';

// Hooks
import { useGetClassTypesEnum } from '../../../api/hooks/useGetClassTypesEnum';

export const ResourceParameter = ({
    parameterId,
    isNew,
    data,
    closeDialog,
    isExistingDoc,
    saveDisabled,
    sessionToken,
    onApply
}) => {
    const classTypesQuery = useGetClassTypesEnum(sessionToken);
    const [compose, updateCompose] = React.useState(false);
    const [edited, setEdited] = React.useState(false);
    const [errors, setErrors] = React.useState({});
    const [fatalError, setFatalError] = React.useState(null);
    const [showDiscardChangesModal, setShowDiscardChangesModal] = React.useState(false);
    const onClose = () => {
        if (edited) {
            setShowDiscardChangesModal(true);
        } else {
            closeDialog();
        }
    };
    const documentState = React.useMemo(() => {
        if (!isNew && data) {
            return data;
        } else {
            return typeof uiConfig.newDoc === 'object' ? uiConfig.newDoc : {};
        }
    }, [data]);

    const formModel = translateUIConfigForAddEdit(uiConfig);
    const validate = (formState) => {
        let errors = {};
        for (let field of formModel) {
            const val = field?.accessorOverride ? field.accessorOverride?.(formState) : formState[field.accessor];

            let { valid, failed } = field.validation(val);
            if (!valid) {
                errors[field.accessor] = 'Invalid Input: ' + failed.join(', ');
            }
            // Add additional validation here;
        }
        // Add relational validation here

        setErrors(errors);
        let valid = Object.keys(errors).length === 0;
        return { valid, failureMsg: '' };
    };

    const prepareDocument = ({ formState, data, isNew }) => {
        const preparedDocument = ((doc) => {
            return {
                ...data,
                ...doc,
                paramId: data.paramId ?? parameterId
            };
        })(formState);

        return isNew
            ? {
                  ...preparedDocument,
                  dtoState: 'NEW',
                  uuid: uuid4()
              }
            : {
                  ...preparedDocument,
                  dtoState: data.dtoState === 'NEW' ? 'NEW' : 'UPDATED'
              };
    };
    const onApplyFormState = (formState) => {
        if (saveDisabled) {
            console.warn('Authoring Interface is currently read-only.');
            return;
        }
        updateCompose(false);
        let update = prepareDocument({ formState, data: documentState, isNew });
        let { valid } = validate(update);
        if (!valid) return;
        if (isExistingDoc?.(update)) {
            setFatalError('Matching document already exists in this list.');
            return;
        }
        onApply(update);
        closeDialog();
    };

    const prepareOptions = () => {
        let optionsOverride = {};
        optionsOverride.classType = classTypesQuery?.data;
        return optionsOverride;
    };

    let optionsOverride = prepareOptions();

    for (let key in optionsOverride) {
        let subIndex = formModel.findIndex((v) => v.accessor === key);
        if (subIndex !== -1) {
            formModel[subIndex].options = optionsOverride[key];
        }
    }

    return (
        <>
            <Dialog close={onClose} size="lg" visible id="criteria-resource-parameter-addEdit">
                <DialogHeader close={onClose}>Resource Parameter Editor</DialogHeader>
                <DialogBody>
                    {fatalError != null && (
                        <FeedbackCard
                            intent={'danger'}
                            feedback={{
                                heading: `An error has occurred.`,
                                message:
                                    typeof fatalError === 'string'
                                        ? fatalError
                                        : `Please wait a few moments before trying your request
again.`
                            }}
                            details={fatalError}
                        />
                    )}
                    <Form
                        formModel={formModel}
                        data={documentState}
                        errors={errors}
                        compose={compose}
                        onApplyFormState={onApplyFormState}
                        setEdited={setEdited}
                        fatalError={fatalError}
                    />
                </DialogBody>
                <DialogFooter>
                    <Button
                        text
                        appearance="primary"
                        onClick={() => updateCompose(true)}
                        disabled={!edited || saveDisabled}
                    >
                        Apply
                    </Button>
                    <Button text appearance="secondary" onClick={onClose}>
                        Cancel
                    </Button>
                </DialogFooter>
            </Dialog>
            {showDiscardChangesModal ? (
                <ConfirmationModal
                    header={`Discard unsaved changes?`}
                    body={
                        'You have unsaved changes which will be discarded upon closing this modal. Do you wish to proceed?'
                    }
                    confirmButtonText={'Yes, Discard Changes'}
                    confirmButtonProps={{ appearance: 'destructive', text: true }}
                    closeDialog={() => setShowDiscardChangesModal(false)}
                    onConfirm={() =>
                        new Promise((resolve) => {
                            closeDialog();
                            resolve();
                        })
                    }
                />
            ) : null}
        </>
    );
};

export default ResourceParameter;

ResourceParameter.propTypes = {
    data: PropTypes.object,
    formModel: PropTypes.arrayOf(PropTypes.object),
    isNew: PropTypes.bool,
    closeDialog: PropTypes.func,
    isExistingDoc: PropTypes.func,
    onApply: PropTypes.func,
    parameterId: PropTypes.string,
    type: PropTypes.string,
    saveDisabled: PropTypes.bool,
    sessionToken: PropTypes.string
};
